/**
 * SequenceBlockCard — one step in a sequence (§11-E).
 *
 * Layout:
 *   - Top row: step number chip + "Email" type + delete button.
 *   - Subject field.
 *   - Rich-text toolbar + highlighted body (shared with T-10 template editor).
 */

import { GripVertical, Mail, Trash2 } from 'lucide-react';
import { RichTextBodyEditor } from '@shared/components/ui/RichTextBodyEditor';

export interface SequenceBlockData {
  id: string;
  step_order: number;
  subject: string;
  body_html: string;
}

interface SequenceBlockCardProps {
  block: SequenceBlockData;
  onChange: (patch: Partial<Pick<SequenceBlockData, 'subject' | 'body_html'>>) => void;
  onDelete: () => void;
  /** Disables delete + prevents editing when sequence is active. */
  readOnly?: boolean;
}

export function SequenceBlockCard({ block, onChange, onDelete, readOnly }: SequenceBlockCardProps) {
  return (
    <div className="rounded-[12px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] overflow-hidden">
      {/* Header strip */}
      <div className="flex items-center justify-between gap-2 border-b border-[var(--vscode-border-primary)] px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0 vscode-text-tertiary" aria-hidden>
            <GripVertical className="w-3.5 h-3.5" />
          </span>
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600/15 px-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            {block.step_order}
          </span>
          <Mail className="w-3.5 h-3.5 vscode-text-tertiary" aria-hidden />
          <span className="text-[12px] font-medium vscode-text-primary">Email</span>
        </div>
        <button
          type="button"
          onClick={onDelete}
          disabled={readOnly}
          title="Delete block"
          className="p-1 rounded vscode-text-tertiary hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Subject */}
      <div className="border-b border-[var(--vscode-border-primary)] px-3 py-2">
        <label className="block text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary mb-1">
          Subject
        </label>
        <input
          type="text"
          value={block.subject}
          onChange={(e) => onChange({ subject: e.target.value })}
          readOnly={readOnly}
          placeholder="Subject line…"
          className="w-full bg-transparent text-[13px] font-medium vscode-text-primary outline-none placeholder:vscode-text-tertiary"
        />
      </div>

      {/* Body editor */}
      <div className="p-3">
        <RichTextBodyEditor
          value={block.body_html}
          onChange={(v) => onChange({ body_html: v })}
          minHeight={200}
          placeholder="Write the email body… use {{VARIABLE_NAME}} for merge tokens"
        />
      </div>
    </div>
  );
}
